import { ItemStatsStatusFlag } from '../enums/ItemStatsStatusFlag';
import { MenuItemStatus } from '../enums/MenuItemStatus';
import { MenuCatg } from '../models/MenuCatg';
import { MenuItem } from '../models/MenuItem';
import { Modifier } from '../models/Modifier';
import { ModifierGrp } from '../models/ModifierGrp';
import { StoreItemStats } from '../models/StoreItemStats';
import { SubModifier } from '../models/SubModifier';
import { SubModifierGrp } from '../models/SubModifierGrp';

// Match the existing loose comparison for optional (null/undefined) item codes.
const codeKey = (code: string): string => code == null ? null : code;

function applyStock(item: MenuItem | Modifier | SubModifier, stats: StoreItemStats): void {
  if (item.itemCode != stats.itemCode) return;

  if (stats.statusFlag == ItemStatsStatusFlag.blocked) {
    item.status = MenuItemStatus.Unavailable;
  }
  if (stats.allowQty > 0) {
    item.qtyBalance = stats.allowQty - (stats.usedQty || 0) - (stats.reservedQty || 0);
    if (item.qtyBalance <= 0) item.status = MenuItemStatus.Unavailable;
  }
}

function processModifiers(groups: ModifierGrp[], stats: StoreItemStats): void {
  for (const group of groups) {
    if (!group.modifiers) continue;
    for (const modifier of group.modifiers) {
      if (modifier.subModifierGrps) {
        for (const subGroup of modifier.subModifierGrps) {
          if (!subGroup.subModifiers) continue;
          for (const subModifier of subGroup.subModifiers) applyStock(subModifier, stats);
          const blocked = subGroup.minSelect >= 1 &&
            subGroup.subModifiers.every(item => item.status == MenuItemStatus.Unavailable);
          subGroup.status = blocked ? MenuItemStatus.Unavailable : (subGroup.status || MenuItemStatus.Available);
        }
        const blocked = modifier.subModifierGrps.some(group =>
          group.minSelect >= 1 && group.status == MenuItemStatus.Unavailable);
        modifier.status = blocked ? MenuItemStatus.Unavailable : (modifier.status || MenuItemStatus.Available);
      }
      applyStock(modifier, stats);
    }
    const blocked = group.minSelect >= 1 &&
      group.modifiers.every(item => item.status == MenuItemStatus.Unavailable);
    group.status = blocked ? MenuItemStatus.Unavailable : (group.status || MenuItemStatus.Available);
  }
}

interface SubGroupUpdate { group: SubModifierGrp; items: SubModifier[]; }
interface ModifierUpdate { item: Modifier; groups: SubGroupUpdate[]; }
interface GroupUpdate { group: ModifierGrp; items: ModifierUpdate[]; }

function appendUpdate<T>(index: Map<string, T[]>, code: string, update: T): void {
  const updates = index.get(code);
  if (updates) updates.push(update);
  else index.set(code, [update]);
}

/** Index only codes in later stock records, retaining each branch's original order. */
function modifierUpdateIndex(groups: ModifierGrp[], stockCodes: Set<string>): Map<string, GroupUpdate[]> {
  const index = new Map<string, GroupUpdate[]>();
  for (const group of groups || []) {
    const groupUpdates = new Map<string, ModifierUpdate[]>();
    for (const item of group.modifiers || []) {
      const itemUpdates = new Map<string, SubGroupUpdate[]>();
      const ownCode = codeKey(item.itemCode);
      if (stockCodes.has(ownCode)) itemUpdates.set(ownCode, []);
      for (const subGroup of item.subModifierGrps || []) {
        const matches = new Map<string, SubModifier[]>();
        for (const subItem of subGroup.subModifiers || []) {
          const code = codeKey(subItem.itemCode);
          if (stockCodes.has(code)) appendUpdate(matches, code, subItem);
        }
        matches.forEach((items, code) => appendUpdate(itemUpdates, code, { group: subGroup, items }));
      }
      itemUpdates.forEach((groups, code) => appendUpdate(groupUpdates, code, { item, groups }));
    }
    groupUpdates.forEach((items, code) => appendUpdate(index, code, { group, items }));
  }
  return index;
}

function processModifierUpdates(updates: GroupUpdate[], stats: StoreItemStats): void {
  for (const { group, items } of updates || []) {
    for (const { item, groups } of items) {
      for (const { group: subGroup, items: subItems } of groups) {
        for (const subItem of subItems) applyStock(subItem, stats);
        if (subGroup.minSelect >= 1 && subGroup.subModifiers.every(child => child.status == MenuItemStatus.Unavailable)) {
          subGroup.status = MenuItemStatus.Unavailable;
        }
      }
      if (item.subModifierGrps?.some(child => child.minSelect >= 1 && child.status == MenuItemStatus.Unavailable)) {
        item.status = MenuItemStatus.Unavailable;
      }
      applyStock(item, stats);
    }
    if (group.minSelect >= 1 && group.modifiers.every(item => item.status == MenuItemStatus.Unavailable)) {
      group.status = MenuItemStatus.Unavailable;
    }
  }
}

/** Mutates the working menu, retaining stock-record, category and item order. */
export function processMenuItemStats(
  stats: StoreItemStats[], categories: MenuCatg[], hideBlocked?: boolean
): MenuCatg[] {
  if (!stats?.length || !categories?.length) return categories;

  const modifierUpdates = new Map<MenuItem, Map<string, GroupUpdate[]>>();
  const laterStockCodes = new Set(stats.slice(1).map(record => codeKey(record.itemCode)));
  for (const category of laterStockCodes.size ? categories : []) {
    for (const item of category.menuItems) {
      modifierUpdates.set(item, modifierUpdateIndex(item.modifierGrps, laterStockCodes));
    }
  }

  const firstItemByCode = new Map<string, MenuItem>();
  let rebuildIndex = true;
  for (let pass = 0; pass < stats.length; pass++) {
    const record = stats[pass];
    // Statuses change through live item references. Rebuild only after category
    // removal, which can change which duplicate code is the first match.
    if (rebuildIndex) {
      firstItemByCode.clear();
      for (const category of categories) {
        for (const item of category.menuItems) {
          const key = codeKey(item.itemCode);
          if (!firstItemByCode.has(key)) firstItemByCode.set(key, item);
        }
      }
    }

    const visibleCategories: MenuCatg[] = [];
    for (const category of categories) {
      for (const item of category.menuItems) {
        if (item.modifierGrps) {
          // First pass also normalizes missing statuses and empty mandatory
          // groups. Later unrelated records cannot change this modifier tree.
          if (pass === 0) {
            processModifiers(item.modifierGrps, record);
          } else {
            // First-pass normalization is complete. Only matching descendants
            // and their ancestors can change; statuses never reset to Available.
            processModifierUpdates(modifierUpdates.get(item)?.get(codeKey(record.itemCode)), record);
          }
          const blocked = item.modifierGrps.some(group =>
            group.minSelect >= 1 && group.status == MenuItemStatus.Unavailable);
          item.status = blocked ? MenuItemStatus.Unavailable : (item.status || MenuItemStatus.Available);
        }

        applyStock(item, record);
        if (item.itemCode == record.itemCode) item.prepareMin = record.prepareTimeMinute || 0;

        // Keep the original empty-array and Unavailable-vs-Hide semantics.
        if (item.variations && item.variations.every(code =>
          firstItemByCode.get(codeKey(code))?.status === MenuItemStatus.Unavailable)) {
          item.status = MenuItemStatus.Unavailable;
        }
        if (hideBlocked && item.status === MenuItemStatus.Unavailable) item.status = MenuItemStatus.Hide;
      }
      if (!hideBlocked || category.menuItems.some(item =>
        item.status === MenuItemStatus.Available && !item.variationOnly)) {
        visibleCategories.push(category);
      }
    }
    rebuildIndex = visibleCategories.length !== categories.length;
    categories = visibleCategories;
  }
  return categories;
}
